import { Head, Link, useForm } from "@inertiajs/react";
import { ArrowLeft, BookOpen, Boxes, MapPin, Plus, UsersRound } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { FormField } from "@/components/FormField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

type Project = { id: number; reference: string; name: string };
type Location = {
  id: number;
  parent_id: number | null;
  name: string;
  code: string | null;
  location_type: string;
  position: number;
  active: boolean;
  unallocated: boolean;
  full_path: string;
};
type Party = {
  id: number;
  name: string;
  party_type: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  active: boolean;
};

export default function ProjectSetup({
  project,
  locations,
  parties,
  location_types,
  party_types,
  can_manage,
}: {
  project: Project;
  locations: Location[];
  parties: Party[];
  location_types: string[];
  party_types: string[];
  can_manage: boolean;
}) {
  const locationForm = useForm({
    name: "",
    code: "",
    location_type: "area",
    parent_id: "",
  });
  const partyForm = useForm({
    name: "",
    party_type: "subcontractor",
    contact_name: "",
    email: "",
    phone: "",
  });

  return (
    <>
      <Head title={`${project.reference} setup`}>
        <meta
          name="description"
          content={`Manage locations and delivery parties for ${project.name}.`}
        />
        <meta property="og:title" content={`${project.reference} setup`} />
        <meta
          property="og:description"
          content={`Manage locations and delivery parties for ${project.name}.`}
        />
      </Head>
      <AppShell>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            href={`/projects/${project.id}`}
            className="inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" /> Project plan
          </Link>
          <div className="flex flex-wrap gap-4">
            <Link href={`/projects/${project.id}/work-packages`} className="inline-flex items-center gap-2">
              <Boxes className="h-4 w-4" /> Work packages
            </Link>
            <Link href="/work-catalogue" className="inline-flex items-center gap-2">
              <BookOpen className="h-4 w-4" /> Work catalogue
            </Link>
          </div>
        </div>
        <header className="mt-5 border-b border-hairline pb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-accent-display">
            {project.reference} · planning foundation
          </p>
          <h1 className="mt-2">Locations & delivery parties</h1>
          <p className="mt-2 max-w-3xl text-ink-muted">
            Define where work happens and who can deliver it. Existing tasks
            remain in the protected Unallocated location until work packages are
            introduced.
          </p>
        </header>

        <section className="mt-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2">
                <MapPin className="h-5 w-5" /> Project locations
              </h2>
              <p className="mt-1 text-ink-muted">
                Nested physical areas such as blocks, floors, rooms and zones.
              </p>
            </div>
            <span className="text-sm text-ink-muted">
              {locations.filter((location) => location.active).length} active
            </span>
          </div>
          <div className="mt-4 space-y-3">
            {locations.map((location) => (
              <LocationRow
                key={location.id}
                project={project}
                location={location}
                locations={locations}
                locationTypes={location_types}
                canManage={can_manage}
              />
            ))}
          </div>
          {can_manage && (
            <form
              className="mt-5 grid gap-3 rounded-md border border-hairline bg-surface p-5 sm:grid-cols-2 lg:grid-cols-[1fr_160px_180px_1fr_auto] lg:items-end"
              onSubmit={(event) => {
                event.preventDefault();
                locationForm.post(`/projects/${project.id}/locations`, {
                  onSuccess: () => locationForm.reset(),
                });
              }}
            >
              <FormField label="Location name" error={locationForm.errors.name}>
                <Input
                  className="mt-1"
                  value={locationForm.data.name}
                  onChange={(event) =>
                    locationForm.setData("name", event.target.value)
                  }
                  placeholder="Ground floor"
                />
              </FormField>
              <FormField label="Code" error={locationForm.errors.code}>
                <Input
                  className="mt-1"
                  value={locationForm.data.code}
                  onChange={(event) =>
                    locationForm.setData("code", event.target.value)
                  }
                  placeholder="GF"
                />
              </FormField>
              <FormField label="Type" error={locationForm.errors.location_type}>
                <Select
                  className="mt-1"
                  value={locationForm.data.location_type}
                  onChange={(event) =>
                    locationForm.setData("location_type", event.target.value)
                  }
                >
                  {location_types.map((type) => (
                    <option key={type} value={type}>
                      {label(type)}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Inside">
                <Select
                  className="mt-1"
                  value={locationForm.data.parent_id}
                  onChange={(event) =>
                    locationForm.setData("parent_id", event.target.value)
                  }
                >
                  <option value="">Project root</option>
                  {locations
                    .filter(
                      (location) => location.active && !location.unallocated,
                    )
                    .map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.full_path}
                      </option>
                    ))}
                </Select>
              </FormField>
              <Button
                type="submit"
                disabled={!locationForm.data.name || locationForm.processing}
              >
                <Plus className="h-4 w-4" /> Add
              </Button>
            </form>
          )}
        </section>

        <section className="mt-10 border-t border-hairline pt-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2">
                <UsersRound className="h-5 w-5" /> Delivery parties
              </h2>
              <p className="mt-1 text-ink-muted">
                Subcontractors, internal crews and supplier-installers available
                to this project.
              </p>
            </div>
            <span className="text-sm text-ink-muted">
              {parties.filter((party) => party.active).length} active
            </span>
          </div>
          <div className="mt-4 space-y-3">
            {parties.map((party) => (
              <PartyRow
                key={party.id}
                project={project}
                party={party}
                partyTypes={party_types}
                canManage={can_manage}
              />
            ))}
            {parties.length === 0 && (
              <p className="rounded-md border border-hairline p-5 text-ink-muted">
                No delivery parties have been added yet.
              </p>
            )}
          </div>
          {can_manage && (
            <form
              className="mt-5 grid gap-3 rounded-md border border-hairline bg-surface p-5 sm:grid-cols-2 lg:grid-cols-[1fr_180px_1fr_1fr_1fr_auto] lg:items-end"
              onSubmit={(event) => {
                event.preventDefault();
                partyForm.post(`/projects/${project.id}/delivery-parties`, {
                  onSuccess: () => partyForm.reset(),
                });
              }}
            >
              <FormField label="Name" error={partyForm.errors.name}>
                <Input
                  className="mt-1"
                  value={partyForm.data.name}
                  onChange={(event) =>
                    partyForm.setData("name", event.target.value)
                  }
                />
              </FormField>
              <FormField label="Type" error={partyForm.errors.party_type}>
                <Select
                  className="mt-1"
                  value={partyForm.data.party_type}
                  onChange={(event) =>
                    partyForm.setData("party_type", event.target.value)
                  }
                >
                  {party_types.map((type) => (
                    <option key={type} value={type}>
                      {label(type)}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Contact">
                <Input
                  className="mt-1"
                  value={partyForm.data.contact_name}
                  onChange={(event) =>
                    partyForm.setData("contact_name", event.target.value)
                  }
                />
              </FormField>
              <FormField label="Email" error={partyForm.errors.email}>
                <Input
                  className="mt-1"
                  type="email"
                  value={partyForm.data.email}
                  onChange={(event) =>
                    partyForm.setData("email", event.target.value)
                  }
                />
              </FormField>
              <FormField label="Phone">
                <Input
                  className="mt-1"
                  value={partyForm.data.phone}
                  onChange={(event) =>
                    partyForm.setData("phone", event.target.value)
                  }
                />
              </FormField>
              <Button
                type="submit"
                disabled={!partyForm.data.name || partyForm.processing}
              >
                <Plus className="h-4 w-4" /> Add
              </Button>
            </form>
          )}
        </section>
      </AppShell>
    </>
  );
}

function LocationRow({
  project,
  location,
  locations,
  locationTypes,
  canManage,
}: {
  project: Project;
  location: Location;
  locations: Location[];
  locationTypes: string[];
  canManage: boolean;
}) {
  const form = useForm({
    name: location.name,
    code: location.code || "",
    location_type: location.location_type,
    parent_id: location.parent_id?.toString() || "",
    position: location.position.toString(),
    active: location.active,
  });
  if (!canManage || location.unallocated)
    return (
      <article className="flex items-center justify-between gap-4 rounded-md border border-hairline p-4">
        <div>
          <p className="font-medium text-ink-display">{location.full_path}</p>
          <p className="mt-1 text-xs text-ink-muted">
            {location.code || "No code"} · {label(location.location_type)}
            {location.unallocated ? " · protected" : ""}
          </p>
        </div>
        <span className="text-xs text-ink-muted">
          {location.active ? "Active" : "Inactive"}
        </span>
      </article>
    );
  const blockedPrefix = `${location.full_path} → `;
  const parentOptions = locations.filter(
    (candidate) =>
      candidate.active &&
      !candidate.unallocated &&
      candidate.id !== location.id &&
      !candidate.full_path.startsWith(blockedPrefix),
  );
  return (
    <form
      className="grid gap-3 rounded-md border border-hairline p-4 sm:grid-cols-2 lg:grid-cols-[1fr_130px_150px_1fr_90px_auto] lg:items-end"
      onSubmit={(event) => {
        event.preventDefault();
        form.patch(`/projects/${project.id}/locations/${location.id}`);
      }}
    >
      <FormField label="Location" error={form.errors.name}>
        <Input
          className="mt-1"
          value={form.data.name}
          onChange={(event) => form.setData("name", event.target.value)}
        />
      </FormField>
      <FormField label="Code" error={form.errors.code}>
        <Input
          className="mt-1"
          value={form.data.code}
          onChange={(event) => form.setData("code", event.target.value)}
        />
      </FormField>
      <FormField label="Type">
        <Select
          className="mt-1"
          value={form.data.location_type}
          onChange={(event) =>
            form.setData("location_type", event.target.value)
          }
        >
          {locationTypes.map((type) => (
            <option key={type} value={type}>
              {label(type)}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Inside">
        <Select
          className="mt-1"
          value={form.data.parent_id}
          onChange={(event) => form.setData("parent_id", event.target.value)}
        >
          <option value="">Project root</option>
          {parentOptions.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.full_path}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Order">
        <Input
          className="mt-1"
          type="number"
          min="0"
          value={form.data.position}
          onChange={(event) => form.setData("position", event.target.value)}
        />
      </FormField>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 font-normal text-ink-body">
          <input
            type="checkbox"
            checked={form.data.active}
            onChange={(event) => form.setData("active", event.target.checked)}
          />{" "}
          Active
        </label>
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          disabled={form.processing}
        >
          Save
        </Button>
      </div>
    </form>
  );
}

function PartyRow({
  project,
  party,
  partyTypes,
  canManage,
}: {
  project: Project;
  party: Party;
  partyTypes: string[];
  canManage: boolean;
}) {
  const form = useForm({
    name: party.name,
    party_type: party.party_type,
    contact_name: party.contact_name || "",
    email: party.email || "",
    phone: party.phone || "",
    active: party.active,
  });
  if (!canManage)
    return (
      <article className="flex items-center justify-between gap-4 rounded-md border border-hairline p-4">
        <div>
          <p className="font-medium text-ink-display">{party.name}</p>
          <p className="mt-1 text-xs text-ink-muted">
            {label(party.party_type)}
            {party.contact_name ? ` · ${party.contact_name}` : ""}
          </p>
        </div>
        <span className="text-xs text-ink-muted">
          {party.active ? "Active" : "Inactive"}
        </span>
      </article>
    );
  return (
    <form
      className="grid gap-3 rounded-md border border-hairline p-4 sm:grid-cols-2 lg:grid-cols-[1fr_170px_1fr_1fr_1fr_auto] lg:items-end"
      onSubmit={(event) => {
        event.preventDefault();
        form.patch(`/projects/${project.id}/delivery-parties/${party.id}`);
      }}
    >
      <FormField label="Name" error={form.errors.name}>
        <Input
          className="mt-1"
          value={form.data.name}
          onChange={(event) => form.setData("name", event.target.value)}
        />
      </FormField>
      <FormField label="Type">
        <Select
          className="mt-1"
          value={form.data.party_type}
          onChange={(event) => form.setData("party_type", event.target.value)}
        >
          {partyTypes.map((type) => (
            <option key={type} value={type}>
              {label(type)}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Contact">
        <Input
          className="mt-1"
          value={form.data.contact_name}
          onChange={(event) => form.setData("contact_name", event.target.value)}
        />
      </FormField>
      <FormField label="Email" error={form.errors.email}>
        <Input
          className="mt-1"
          type="email"
          value={form.data.email}
          onChange={(event) => form.setData("email", event.target.value)}
        />
      </FormField>
      <FormField label="Phone">
        <Input
          className="mt-1"
          value={form.data.phone}
          onChange={(event) => form.setData("phone", event.target.value)}
        />
      </FormField>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 font-normal text-ink-body">
          <input
            type="checkbox"
            checked={form.data.active}
            onChange={(event) => form.setData("active", event.target.checked)}
          />{" "}
          Active
        </label>
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          disabled={form.processing}
        >
          Save
        </Button>
      </div>
    </form>
  );
}

function label(value: string) {
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
